/**
 * DataTable – the one table every analytics screen uses.
 *
 * Sortable columns, optional search, pagination past a page of rows, and on
 * small screens each row becomes a card instead of a table that scrolls
 * sideways. Columns declare how to sort themselves, so a "—" never sorts as if
 * it were a number.
 */

import { useMemo, useState, type ReactNode } from 'react';
import { ArrowDown, ArrowUp, ChevronsUpDown, Search } from 'lucide-react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';

export interface Column<T> {
  key: string;
  header: string;
  cell: (row: T) => ReactNode;
  /** Value to sort by; null sorts last in either direction. Omit to make the column unsortable. */
  sortValue?: (row: T) => string | number | null;
  align?: 'left' | 'right';
  /** Hide in the mobile card view (e.g. secondary figures, sparklines). */
  hideOnMobile?: boolean;
  className?: string;
}

export function DataTable<T>({
  rows,
  columns,
  rowKey,
  initialSort,
  searchPlaceholder,
  searchText,
  pageSize = 20,
  onRowClick,
  empty,
  toolbar,
}: {
  rows: T[];
  columns: Column<T>[];
  rowKey: (row: T) => string;
  initialSort?: { key: string; direction: 'asc' | 'desc' };
  /** Enables the search box. */
  searchPlaceholder?: string;
  /** Text a row is searched by. Required with searchPlaceholder. */
  searchText?: (row: T) => string;
  pageSize?: number;
  onRowClick?: (row: T) => void;
  empty?: ReactNode;
  /** Extra controls beside the search box, such as filters. */
  toolbar?: ReactNode;
}) {
  const [sort, setSort] = useState(initialSort ?? null);
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(0);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = q && searchText ? rows.filter((r) => searchText(r).toLowerCase().includes(q)) : rows;
    const column = sort && columns.find((c) => c.key === sort.key);
    if (column?.sortValue) {
      const dir = sort!.direction === 'asc' ? 1 : -1;
      list = [...list].sort((a, b) => {
        const va = column.sortValue!(a);
        const vb = column.sortValue!(b);
        if (va === null && vb === null) return 0;
        if (va === null) return 1;
        if (vb === null) return -1;
        if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * dir;
        return String(va).localeCompare(String(vb), undefined, { sensitivity: 'base', numeric: true }) * dir;
      });
    }
    return list;
  }, [rows, columns, sort, query, searchText]);

  const pageCount = Math.max(1, Math.ceil(visible.length / pageSize));
  const currentPage = Math.min(page, pageCount - 1);
  const pageRows = visible.slice(currentPage * pageSize, (currentPage + 1) * pageSize);

  const toggleSort = (key: string) => {
    setSort((s) => (s?.key === key ? { key, direction: s.direction === 'asc' ? 'desc' : 'asc' } : { key, direction: 'asc' }));
  };

  const [primary, ...rest] = columns;

  return (
    <div>
      {(searchPlaceholder || toolbar) && (
        <div className="mb-3 flex flex-wrap items-center gap-2">
          {searchPlaceholder && (
            <div className="relative w-full sm:w-64">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" aria-hidden />
              <Input
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setPage(0);
                }}
                placeholder={searchPlaceholder}
                aria-label={searchPlaceholder}
                className="h-9 pl-8 text-sm"
              />
            </div>
          )}
          {toolbar}
        </div>
      )}

      {visible.length === 0 ? (
        empty ?? <p className="py-8 text-center text-sm text-muted-foreground">{query ? 'No matches.' : 'Nothing to show yet.'}</p>
      ) : (
        <>
          {/* Desktop and tablet: a table */}
          <div className="hidden overflow-x-auto md:block">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                  {columns.map((c) => {
                    const active = sort?.key === c.key;
                    const Icon = !active ? ChevronsUpDown : sort!.direction === 'asc' ? ArrowUp : ArrowDown;
                    return (
                      <th
                        key={c.key}
                        scope="col"
                        aria-sort={active ? (sort!.direction === 'asc' ? 'ascending' : 'descending') : undefined}
                        className={`whitespace-nowrap px-3 py-2.5 font-semibold ${c.align === 'right' ? 'text-right' : ''} ${c.className ?? ''}`}
                      >
                        {c.sortValue ? (
                          <button
                            type="button"
                            onClick={() => toggleSort(c.key)}
                            className={`inline-flex items-center gap-1 hover:text-foreground ${active ? 'text-foreground' : ''}`}
                          >
                            {c.header}
                            <Icon className="h-3 w-3" aria-hidden />
                          </button>
                        ) : (
                          c.header
                        )}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {pageRows.map((row) => (
                  <tr
                    key={rowKey(row)}
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                    className={`border-b border-border/60 last:border-0 ${onRowClick ? 'cursor-pointer hover:bg-muted/40' : ''}`}
                  >
                    {columns.map((c) => (
                      <td
                        key={c.key}
                        className={`px-3 py-2.5 align-middle ${c.align === 'right' ? 'text-right tabular-nums' : ''} ${c.className ?? ''}`}
                      >
                        {c.cell(row)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Phones: a card per row */}
          <ul className="space-y-2 md:hidden">
            {pageRows.map((row) => (
              <li
                key={rowKey(row)}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                className={`rounded-lg border border-border p-3 ${onRowClick ? 'cursor-pointer active:bg-muted/40' : ''}`}
              >
                <div className="mb-2 text-sm">{primary.cell(row)}</div>
                <dl className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs">
                  {rest
                    .filter((c) => !c.hideOnMobile)
                    .map((c) => (
                      <div key={c.key} className="min-w-0">
                        <dt className="text-[10px] uppercase tracking-wide text-muted-foreground">{c.header}</dt>
                        <dd className="truncate text-foreground">{c.cell(row)}</dd>
                      </div>
                    ))}
                </dl>
              </li>
            ))}
          </ul>

          {pageCount > 1 && (
            <div className="mt-3 flex items-center justify-between text-xs text-muted-foreground">
              <span>
                {currentPage * pageSize + 1}–{Math.min((currentPage + 1) * pageSize, visible.length)} of {visible.length}
              </span>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" disabled={currentPage === 0} onClick={() => setPage(currentPage - 1)}>
                  Previous
                </Button>
                <Button size="sm" variant="outline" disabled={currentPage >= pageCount - 1} onClick={() => setPage(currentPage + 1)}>
                  Next
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
